import React, { useCallback, useEffect, useState } from 'react';

import { DeleteOutlined, MenuOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Popconfirm, Switch, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import { LiteAppPostData, LiteAppPostDataParam } from '@/Interfaces/I_LiteApp';

import { setLoading } from '@/Redux/slices/AccountSlice';
import {
  deleteLiteAppPost,
  getLiteAppPosts,
  updateLiteAppPosts,
} from '@/Redux/slices/LiteAppSlice';
import { getPostDetail } from '@/Redux/slices/ModalSlice';

import Modal from '@/Modules/modal';
import UserRoleLabel from '@/Modules/userRoleLabel';

import CreateLiteAppPostModal from '@/Components/CreateLiteAppPostModal';

import { timeTranslate } from '@/Utils/timeTranslate';

import fileText from '@/Images/icon/fileText.svg';

import type { DragEndEvent } from '@dnd-kit/core';

interface LiteAppUI {
  uuid: string;
  title: string;
  user: {
    uuid: string;
    name: string;
    kocUserRole: string;
  };
  creationDate: number;
  lastModifiedDate: number;
  disabled: boolean;
  priority: number;
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row = ({ children, ...props }: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === 'sort') {
          return React.cloneElement(child as React.ReactElement, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{ touchAction: 'none', cursor: 'move' }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const LiteApp = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.liteApp.loading);
  const { liteAppPosts, version } = useAppSelector((state) => state.liteApp.value);
  const userName = useAppSelector((state) => state.account.user.username);

  const [pristine, setPristine] = useState(true);
  const [isModalVisible, setModalVisible] = useState(false);
  const [isPostModalVisible, setPostModalVisible] = useState(false);

  const [liteAppUIs, setLiteAppUIs] = useState<LiteAppUI[]>([]);

  const initLiteAppUIs = useCallback(() => {
    const sortByPriority = liteAppPosts
      .toSorted((p1: LiteAppPostData, p2: LiteAppPostData) => p1.priority - p2.priority)
      .map((post: LiteAppPostData) => ({
        uuid: post.uuid,
        title: post.title,
        user: post.user,
        creationDate: post.creationDate,
        lastModifiedDate: post.lastModifiedDate,
        disabled: post.disabled,
        priority: post.priority,
      }));

    setLiteAppUIs(sortByPriority || []);
    setPristine(true);
    setLoading(false);
  }, [liteAppPosts]);

  useEffect(() => {
    if (userName) {
      dispatch(getLiteAppPosts());
    }
  }, [dispatch, userName]);

  useEffect(() => {
    if (!loading) {
      initLiteAppUIs();
    }
  }, [initLiteAppUIs, loading, liteAppPosts]);

  const onUpdateLiteAppUIs = useCallback(() => {
    const updatedLiteAppPosts: LiteAppPostDataParam[] = liteAppUIs.map((data, index) => ({
      uuid: data.uuid,
      priority: index + 1,
    }));

    dispatch(updateLiteAppPosts({ data: updatedLiteAppPosts, version }));
  }, [liteAppUIs, dispatch, version]);

  const onResetLiteAppUIs = useCallback(() => {
    initLiteAppUIs();
    setPristine(true);
  }, [initLiteAppUIs]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setLiteAppUIs((previous) => {
        const activeIndex = previous.findIndex((i) => i.uuid === active.id);
        const overIndex = previous.findIndex((i) => i.uuid === over?.id);
        return arrayMove(previous, activeIndex, overIndex);
      });
      setPristine(false);
    }
  };

  const columns: ColumnsType<LiteAppUI> = [
    {
      key: 'sort',
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
    },
    {
      title: 'Post ID',
      dataIndex: 'uuid',
      render: (uuid: string) => (
        <>
          {uuid}
          <img
            aria-hidden
            src={fileText}
            alt="photo"
            className="cursor-pointer ml-2"
            onClick={() => {
              setPostModalVisible(true);
              dispatch(getPostDetail(uuid));
            }}
          />
        </>
      ),
    },
    {
      title: 'Post Title',
      dataIndex: 'title',
      width: '25%',
    },
    {
      title: 'Creator',
      dataIndex: ['user', 'name'],
    },
    {
      title: 'Role',
      dataIndex: ['user', 'kocUserRole'],
      ellipsis: {
        showTitle: false,
      },
      render: (profileTag: string) => (
        <UserRoleLabel
          userRoleStr={
            profileTag as
              | 'CUSTOMER'
              | 'OFFICIAL'
              | 'CONTENT_PROVIDER'
              | 'MERCHANT'
              | 'HKTV_SHOPS_MERCHANT'
              | 'YOUTUBER'
              | 'STREAMER'
          }
        />
      ),
    },
    {
      title: 'Date Added',
      dataIndex: 'creationDate',
      key: 'creationDate',
      render: (date: number) => <div className="flex">{timeTranslate(date)}</div>,
    },
    {
      title: 'Last Modified Date',
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      render: (date: number) => <div className="flex">{timeTranslate(date)}</div>,
    },
    {
      title: 'Disabled?',
      dataIndex: 'disabled',
      key: 'disabled',
      render: (value: boolean) => <Switch disabled checked={value} />,
    },
    {
      title: 'Removed?',
      key: 'delete',
      render: (liteAppPost) => {
        const { uuid } = liteAppPost;
        return (
          <Popconfirm
            title="Confirm to remove?"
            onConfirm={() => dispatch(deleteLiteAppPost({ data: uuid, version }))}
            okText="Yes"
            cancelText="No"
          >
            <Button danger>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div className="h-auto bg-white p-6 pb-2">
      <div className="flex items-center pb-4 justify-between">
        <div className="flex">
          <div className="pr-2">
            <Button type="primary" onClick={onUpdateLiteAppUIs} disabled={pristine}>
              Update
            </Button>
          </div>
          <div>
            <Button type="primary" onClick={onResetLiteAppUIs} disabled={pristine}>
              Reset
            </Button>
          </div>
        </div>
        <div className="justify-self-end">
          <Button type="primary" onClick={() => setModalVisible(true)}>
            Add
          </Button>
        </div>
      </div>

      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          // rowKey array
          items={liteAppUIs.map((i) => i.uuid)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            components={{
              body: {
                row: Row,
              },
            }}
            rowKey="uuid"
            columns={columns}
            dataSource={liteAppUIs}
            pagination={false}
            loading={loading}
          />
        </SortableContext>
      </DndContext>

      <CreateLiteAppPostModal
        isModalVisible={isModalVisible}
        setModalVisible={setModalVisible}
        postLength={liteAppPosts.length}
      />
      <Modal
        visible={isPostModalVisible}
        onCancel={() => setPostModalVisible(false)}
        modalType="postDetail"
      />
    </div>
  );
};

export default LiteApp;
